/**
 * Table cells hold a mini block list so "/" works the same as in the page.
 * Legacy cells are plain HTML strings; normalize them to a text block on read.
 *
 * Nested tables / columns / callouts / snippets stay out of cells — those
 * types land beside the host table instead of nesting inside a cell.
 */

import { createBlock } from './blockUtils';
import { htmlToText } from './caretUtils';

export const CELL_SLASH_EXCLUDE = [
  'table',
  'row',
  'variables',
  'callout',
  'quote',
  'toggle',
  'choice',
  'snippet',
];

export const TABLE_COL_MIN = 80;

/** Relative weights (any positive numbers). Missing / empty → null (equal cols). */
export const normalizeColumnWidths = (raw, colCount) => {
  if (!Array.isArray(raw) || raw.length === 0 || colCount < 1) return null;
  const finite = raw
    .slice(0, colCount)
    .map((n) => Number(n))
    .filter((n) => Number.isFinite(n) && n > 0);
  const fill = finite.length
    ? finite.reduce((a, b) => a + b, 0) / finite.length
    : 1;
  const next = [];
  for (let i = 0; i < colCount; i++) {
    const n = Number(raw[i]);
    next.push(Number.isFinite(n) && n > 0 ? n : fill);
  }
  return next;
};

/** Grow `leftIndex` by `deltaPx`, take it from the next column. Total stays the same. */
export const adjustAdjacentTableWidths = (widths, leftIndex, deltaPx, minPx = TABLE_COL_MIN) => {
  if (!widths || leftIndex < 0 || leftIndex >= widths.length - 1) return widths;
  const next = [...widths];
  let d = deltaPx;
  if (next[leftIndex] + d < minPx) d = minPx - next[leftIndex];
  if (next[leftIndex + 1] - d < minPx) d = next[leftIndex + 1] - minPx;
  next[leftIndex] += d;
  next[leftIndex + 1] -= d;
  return next;
};

export const emptyTableData = () => ({
  headers: [createBlock('text', 'Column 1')],
  rows: [[createBlock('text')]],
});

export const columnWidthsEqual = (a, b) => {
  if (!a && !b) return true;
  if (!a || !b || a.length !== b.length) return false;
  return a.every((w, i) => w === b[i]);
};

export const isCellBlocks = (value) =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every(
    (b) => b && typeof b === 'object' && typeof b.id === 'string' && typeof b.type === 'string'
  );

export const emptyCellBlocks = () => [createBlock('text')];

export const normalizeCellToBlocks = (value) => {
  if (isCellBlocks(value)) return value;
  if (typeof value === 'string') return [createBlock('text', value)];
  return emptyCellBlocks();
};

export const tableNeedsCellMigration = (raw) => {
  if (!raw) return false;
  const cells = [...(raw.headers || []), ...(raw.rows || []).flat()];
  return cells.some((cell) => !isCellBlocks(cell));
};

export const normalizeTableData = (raw) => {
  const src = raw || {};
  const rawHeaders = src.headers?.length ? src.headers : [''];
  const headers = rawHeaders.map((cell) => normalizeCellToBlocks(cell));
  const width = headers.length;
  const rawRows = src.rows?.length ? src.rows : [new Array(width).fill('')];
  const rows = rawRows.map((row) => {
    const cells = (row || []).map((cell) => normalizeCellToBlocks(cell));
    while (cells.length < width) cells.push(emptyCellBlocks());
    return cells.slice(0, width);
  });
  const columnWidths = normalizeColumnWidths(src.columnWidths, width);
  return columnWidths ? { headers, rows, columnWidths } : { headers, rows };
};

const stripIds = (blocks) =>
  (blocks || []).map((b) => ({
    type: b.type,
    content: b.content || '',
    props: b.props || {},
    children: b.children?.length ? stripIds(b.children) : [],
  }));

const asBlocks = (value) =>
  isCellBlocks(value) ? value : [{ type: 'text', content: value || '', props: {}, children: [] }];

export const cellContentEqual = (a, b) =>
  JSON.stringify(stripIds(asBlocks(a))) === JSON.stringify(stripIds(asBlocks(b)));

export const tableContentEqual = (a, b) => {
  if (!a || !b) return a === b;
  if ((a.headers || []).length !== (b.headers || []).length) return false;
  if ((a.rows || []).length !== (b.rows || []).length) return false;
  if (a.headers.some((h, i) => !cellContentEqual(h, b.headers[i]))) return false;
  if (
    !a.rows.every(
      (row, ri) =>
        (row || []).length === (b.rows[ri] || []).length &&
        (row || []).every((c, ci) => cellContentEqual(c, b.rows[ri][ci]))
    )
  ) {
    return false;
  }
  return columnWidthsEqual(
    normalizeColumnWidths(a.columnWidths, (a.headers || []).length),
    normalizeColumnWidths(b.columnWidths, (b.headers || []).length)
  );
};

export const cellToInlineHtml = (value) => {
  if (isCellBlocks(value)) {
    return value
      .map((b) => b.content || '')
      .filter(Boolean)
      .join(' ');
  }
  return typeof value === 'string' ? value : '';
};

export const cellToPlainText = (value) => {
  if (isCellBlocks(value)) {
    return value
      .map((b) => htmlToText(b.content || '').trim())
      .filter(Boolean)
      .join(' ');
  }
  return htmlToText(value || '').trim();
};

export const mapCellStringContent = (value, fn) => {
  if (isCellBlocks(value)) {
    let changed = false;
    const next = value.map((b) => {
      if (typeof b.content !== 'string') return b;
      const content = fn(b.content);
      if (content === b.content) return b;
      changed = true;
      return { ...b, content };
    });
    return { value: changed ? next : value, changed };
  }
  const content = fn(value || '');
  return { value: content, changed: content !== (value || '') };
};

/** Page blocks plus blocks living inside table cells. */
export const flattenBlocksDeep = (blocks, into = []) => {
  for (const block of blocks || []) {
    into.push(block);
    if (block.children?.length) flattenBlocksDeep(block.children, into);
    if (block.type === 'table' && block.props?.tableData) {
      const cells = [
        ...(block.props.tableData.headers || []),
        ...(block.props.tableData.rows || []).flat(),
      ];
      for (const cell of cells) {
        if (isCellBlocks(cell)) flattenBlocksDeep(cell, into);
      }
    }
  }
  return into;
};

export const mapBlocksInTableData = (tableData, mapList) => {
  if (!tableData) return { tableData, changed: false };
  let changed = false;
  const headers = (tableData.headers || []).map((cell) => {
    if (!isCellBlocks(cell)) return cell;
    const next = mapList(cell);
    if (next !== cell) changed = true;
    return next;
  });
  const rows = (tableData.rows || []).map((row) =>
    (row || []).map((cell) => {
      if (!isCellBlocks(cell)) return cell;
      const next = mapList(cell);
      if (next !== cell) changed = true;
      return next;
    })
  );
  return { tableData: changed ? { ...tableData, headers, rows } : tableData, changed };
};

export const mapTableCellStrings = (tableData, fn) => {
  if (!tableData) return { tableData, changed: false };
  let changed = false;
  const headers = (tableData.headers || []).map((cell) => {
    const r = mapCellStringContent(cell, fn);
    if (r.changed) changed = true;
    return r.value;
  });
  const rows = (tableData.rows || []).map((row) =>
    (row || []).map((cell) => {
      const r = mapCellStringContent(cell, fn);
      if (r.changed) changed = true;
      return r.value;
    })
  );
  return { tableData: changed ? { ...tableData, headers, rows } : tableData, changed };
};
